import React, { useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  MouseSensor,
} from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DragIcon, QuestionMarkIcon, TrashIcon } from '@fourthwall/icons'
import { Icon } from '../Icon'
import { Loader } from '../Loader'
import * as S from './SortableGallery.styled'
import type {
  DataItem,
  SortableGalleryContainerProps,
  SortableGalleryElementProps,
  SortableGalleryProps,
} from './types'
import { useMediaQueryContext } from '../../providers/MediaQueryProvider'
import { useModalContext } from '../../providers/ModalProvider'
import { Divider } from '../Divider'
import MobileContextMenu from './MobileContextMenu/MobileContextMenu'
import { Tooltip } from '../Tooltip'

const SortableGalleryElement = React.memo(
  ({
    value,
    isDisabled,
    inactive,
    onView,
    onRemove,
    onMoveUp,
    onMoveDown,
  }: SortableGalleryElementProps) => {
    const media = useMediaQueryContext()
    const { open } = useModalContext()

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: value.id,
      disabled: isDisabled,
    })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    }

    const handleImageClick = React.useCallback(
      (e: React.MouseEvent) => {
        // Prevent click when dragging ends, very important! Otherwise, after image removal it will call onView
        if (e.detail === 0) return

        if (!media.tablet) {
          open(MobileContextMenu, {
            onView: () => onView?.(value),
            onMoveUp,
            onMoveDown,
            onDelete: () => onRemove?.(value),
          })
        } else if (onView) {
          onView(value)
        }
      },
      [media.tablet, onMoveDown, onMoveUp, onRemove, onView, open, value],
    )

    const handleTrashClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        onRemove?.(value)
      },
      [onRemove, value],
    )

    if (value.isUploading) {
      return (
        <S.Item>
          <S.LoaderContainer>
            <div>Uploading...</div>
            <Loader />
          </S.LoaderContainer>
        </S.Item>
      )
    }

    return (
      <S.Item ref={setNodeRef} style={style} disabled={isDisabled} {...attributes} {...listeners}>
        <S.ImageContainer disabled={isDisabled} onClick={isDisabled ? undefined : handleImageClick}>
          <S.Image src={value.url} inactive={inactive} />

          {onRemove && (
            <S.TrashButtonWrapper>
              <S.TrashButton onClick={handleTrashClick}>
                <Icon component={TrashIcon} height={12} width={12} />
              </S.TrashButton>
            </S.TrashButtonWrapper>
          )}

          {!isDisabled && (
            <S.DragAndDropAnchorWrapper>
              <S.DragAndDropAnchorButton>
                <Icon component={DragIcon} height={12} width={12} />
              </S.DragAndDropAnchorButton>
            </S.DragAndDropAnchorWrapper>
          )}
        </S.ImageContainer>
      </S.Item>
    )
  },
)

export default SortableGalleryElement

const SortableGalleryContainer = ({
  data,
  uploadZone,
  inactiveStartIndex,
  disabled,
  onView,
  onRemove,
  onSortChange,
}: SortableGalleryContainerProps) => {
  const media = useMediaQueryContext()
  const activeItems = inactiveStartIndex ? data.slice(0, inactiveStartIndex) : data
  const inactiveItems = inactiveStartIndex ? data.slice(inactiveStartIndex) : []

  let columnCount
  if (inactiveStartIndex && media.tablet) {
    columnCount = activeItems.length >= 7 ? 6 : 4
  }

  const handleMoveUp = (index: number) => {
    const canMoveUp = index > 0

    if (canMoveUp && onSortChange) {
      onSortChange(index, index - 1)
    }
  }

  const handleMoveDown = (index: number) => {
    const canMoveDown = index < data.length - 1

    if (canMoveDown && onSortChange) {
      onSortChange(index, index + 1)
    }
  }

  return (
    <S.Container columns={columnCount}>
      <SortableContext items={data.map((item) => item.id)}>
        {activeItems.length === 0 && uploadZone}

        {activeItems.length > 0 &&
          activeItems.map((value: DataItem, index) => (
            <S.ItemWrapper key={value.id}>
              <S.Placeholder />
              <SortableGalleryElement
                value={value}
                disabled={disabled || !media.tablet}
                isDisabled={disabled}
                onView={onView}
                onRemove={onRemove}
                onMoveDown={() => handleMoveDown(index)}
                onMoveUp={() => handleMoveUp(index)}
              />
            </S.ItemWrapper>
          ))}

        {uploadZone && activeItems.length !== 0 && inactiveItems.length === 0 && (
          <S.UploadZone>{uploadZone}</S.UploadZone>
        )}

        {inactiveStartIndex && inactiveItems.length > 0 && (
          <S.Divider>
            <Divider
              label={
                <S.DividerText>
                  Inactive photos{' '}
                  <Tooltip reference={<Icon component={QuestionMarkIcon} height={12} />} fluid>
                    <div style={{ width: '220px' }}>
                      You can feature up to 12 photos in your product gallery. The remaining photos
                      (marked as inactive) won't be displayed.
                    </div>
                  </Tooltip>
                </S.DividerText>
              }
            />
          </S.Divider>
        )}

        {inactiveStartIndex &&
          inactiveItems.length > 0 &&
          inactiveItems.map((value: DataItem, elementIndex) => {
            const index = elementIndex + inactiveStartIndex

            return (
              <S.ItemWrapper key={value.id}>
                <S.Placeholder />
                <SortableGalleryElement
                  inactive
                  value={value}
                  disabled={disabled || !media.tablet}
                  isDisabled={disabled}
                  onView={onView}
                  onRemove={onRemove}
                  onMoveDown={() => handleMoveDown(index)}
                  onMoveUp={() => handleMoveUp(index)}
                />
              </S.ItemWrapper>
            )
          })}

        {uploadZone && inactiveItems.length > 0 && <S.UploadZone>{uploadZone}</S.UploadZone>}
      </SortableContext>
    </S.Container>
  )
}

export const SortableGallery = ({
  data,
  uploadZone,
  disabled = false,
  inactiveStartIndex,
  onView,
  onRemove,
  onSortChange,
}: SortableGalleryProps) => {
  const [isDragged, setIsDragged] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: disabled ? Infinity : 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: disabled ? Infinity : 5,
      },
    }),
  )

  const handleDragStart = () => {
    if (disabled) return
    setIsDragged(true)
  }

  const handleDragEnd = (event: any) => {
    if (disabled) return
    const { active, over } = event

    setIsDragged(false)

    if (active?.id && over?.id && active?.id !== over?.id) {
      const oldIndex = data.findIndex((item) => item?.id === active?.id)
      const newIndex = data.findIndex((item) => item?.id === over?.id)

      if (oldIndex !== -1 && newIndex !== -1 && onSortChange) {
        onSortChange(oldIndex, newIndex)
      }
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableGalleryContainer
        data={data}
        uploadZone={uploadZone}
        inactiveStartIndex={inactiveStartIndex}
        disabled={disabled}
        isDragged={isDragged}
        onView={onView}
        onRemove={onRemove}
        onSortChange={onSortChange}
      />
    </DndContext>
  )
}

SortableGallery.UploadZoneWrapper = S.UploadZoneWrapper
